import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { FeedbackQuestionsResponse, MyLearningFilters, MyLearningResponse } from '@lib/interfaces';
import { MonoTypeOperatorFunction, Observable, catchError, throwError } from 'rxjs';

export interface Course {
    id: string;
    name: string;
    courseType: 'Open' | 'In-House';
    courseId: string;
    status: string;
    statusInformation: string;
    startDate: string;
}

export interface Program {
    id: string;
    name: string;
    type: string;
    status: string;
    statusInfo: string;
    startDate: string;
}

export interface PaginatedResponse<T> {
    data: T[];
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
}

export interface CourseFilters {
    status?: string;
    courseType?: string;
    searchTerm?: string;
    page?: number;
    pageSize?: number;
}

export interface ProgramFilters {
    searchTerm?: string;
    page?: number;
    pageSize?: number;
}

@Injectable({
    providedIn: 'root',
})
export class MyLearningService {
    private readonly http = inject(HttpClient);
    private readonly apiUrl = environment.API_URL;

    /**
     * Get my-learning data with pagination and filters
     * This method handles both courses and programs data
     */
    getMyLearningData(filters: MyLearningFilters = {}): Observable<MyLearningResponse> {
        let params = new HttpParams();

        if (filters.searchParam) {
            params = params.set('searchParam', filters.searchParam);
        }
        if (filters.isParent !== undefined) {
            params = params.set('isParent', filters.isParent.toString());
        }
        if (filters.page !== undefined) {
            params = params.set('page', filters.page.toString());
        }
        if (filters.size !== undefined) {
            params = params.set('size', filters.size.toString());
        }
        if (filters.sortField) {
            params = params.set('sortField', filters.sortField);
        }
        if (filters.sortDirection) {
            params = params.set('sortDirection', filters.sortDirection);
        }

        return this.http
            .get<MyLearningResponse>(`${this.apiUrl}/students/my-learning`, { params })
            .pipe(this.handleHttpError('Failed to load my learning data'));
    }

    /**
     * Get feedback questions for a specific course
     * @param courseId - The ID of the course to get feedback questions for
     * @returns Observable of FeedbackQuestionsResponse
     */
    getFeedbackQuestions(courseId: string): Observable<FeedbackQuestionsResponse> {
        return this.http
            .get<FeedbackQuestionsResponse>(`${this.apiUrl}/students/feedback/course/${courseId}`)
            .pipe(this.handleHttpError('Failed to fetch feedback questions'));
    }

    /**
     * Submit feedback for a specific course
     * @param payload - The feedback submission payload
     * @returns Observable of the API response
     */
    submitFeedback(payload: {
        courseId: string;
        answers: Array<{ questionId: number; rating: number }>;
        review?: string;
        isAnonymous: boolean;
    }): Observable<unknown> {
        return this.http
            .post<unknown>(`${this.apiUrl}/students/feedback`, payload)
            .pipe(this.handleHttpError('Failed to submit feedback'));
    }

    private handleHttpError<T>(fallbackMessage: string): MonoTypeOperatorFunction<T> {
        return catchError((error: unknown) => this.propagateError(error, fallbackMessage));
    }

    private propagateError(error: unknown, fallbackMessage: string): Observable<never> {
        if (error instanceof HttpErrorResponse) {
            const message = this.resolveHttpErrorMessage(error, fallbackMessage);
            console.error(fallbackMessage, error);
            return throwError(() => new Error(message));
        }
        if (error instanceof Error) {
            console.error(fallbackMessage, error);
            return throwError(() => error);
        }
        console.error(fallbackMessage, error);
        return throwError(() => new Error(fallbackMessage));
    }

    private resolveHttpErrorMessage(error: HttpErrorResponse, fallback: string): string {
        if (error.error instanceof ErrorEvent) {
            return error.error.message || 'A network error occurred';
        }
        const body = error.error;
        if (body && typeof body === 'object' && 'message' in body) {
            const msg = (body as { message: unknown }).message;
            if (typeof msg === 'string' && msg.length > 0) {
                return msg;
            }
        }
        if (typeof body === 'string' && body.trim().length > 0) {
            return body;
        }
        if (error.status === 0) {
            return 'Unable to reach the server. Check your connection and try again.';
        }
        return error.message || fallback;
    }
}
